<script setup lang="ts">
import { computed, useAttrs } from 'vue'

import AppIcon from '@ui/app-icon/AppIcon.vue'

import type { AppBadgeProps } from './types'

const props = withDefaults(defineProps<AppBadgeProps>(), {
  type: 'label',
  variant: 'green',
  size: 'md',
  text: '',
  leftIcon: '',
  rightIcon: '',
})

const classes = computed(() => [
  `type-${props.type}`,
  `variant-${props.variant}`,
  `size-${props.size}`,
])

const fontClasses = computed(() => {
  const map: Record<string, string> = {
    sm: 'font-badge',
  }
  return map[props.size] || 'font-medium-body'
})

const iconSize = computed(() => {
  const map: Record<string, string> = {
    sm: '12px',
  }
  return map[props.size] || '16px'
})

const attrs = useAttrs()
</script>

<template>
  <span
    class="app-badge"
    :class="[classes, attrs.class]"
    data-test-id="app-badge"
    v-bind="attrs"
  >
    <template v-if="type === 'count'">
      <span class="app-badge-count font-badge" data-test-id="app-badge-count">
        <slot>
          {{ text }}
        </slot>
      </span>
    </template>

    <template v-else-if="type === 'label'">
      <div class="app-badge-label" data-test-id="app-badge-label">
        <span v-if="leftIcon" class="app-badge-label-icon">
          <app-icon :name="leftIcon" :size="iconSize" />
        </span>
        <span class="app-badge-label-text" :class="fontClasses">
          <slot>
            {{ text }}
          </slot>
        </span>
        <span v-if="rightIcon" class="app-badge-label-icon">
          <app-icon :name="rightIcon" :size="iconSize" />
        </span>
      </div>
    </template>

    <template v-else>
      <span
        class="app-badge-dot"
        data-test-id="app-badge-dot"
        aria-hidden="true"
      />
    </template>
  </span>
</template>

<style scoped lang="scss">
.app-badge {
  --badge-gap: 4px;
  --badge-min-height: 24px;
  --badge-padding: 2px 8px;
  --badge-radius: 360px;
  --badge-icon-size: 16px;

  $root: &;

  display: inline-flex;
  vertical-align: middle;

  &-count,
  &-label,
  &-dot,
  &-label-icon {
    background-color: var(--badge-bg);
    color: var(--badge-color);
  }

  &-count {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-width: 20px;
    height: 20px;
    padding: 0 3px;
    border-radius: 999px;
    box-sizing: border-box;
    font-weight: 600;
  }

  &-label {
    display: inline-flex;
    align-items: center;
    min-height: var(--badge-min-height);
    padding: var(--badge-padding);
    column-gap: var(--badge-gap);
    border-radius: var(--badge-radius);
    font-weight: 500;

    &-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: var(--badge-icon-size);
      height: var(--badge-icon-size);
      font-size: var(--badge-icon-size);
      line-height: var(--badge-icon-size);
    }
  }

  &-dot {
    display: inline-flex;
    width: 4px;
    height: 4px;
    border-radius: 100%;
  }

  &.variant-green {
    --badge-bg: var(--s-green);
    --badge-color: var(--absolute-white);
  }

  &.variant-green-bright {
    --badge-bg: var(--s-green-bright);
    --badge-color: var(--absolute-black);
  }

  &.variant-yellow {
    --badge-bg: var(--s-yellow);
    --badge-color: var(--absolute-black);
  }

  &.variant-purple {
    --badge-bg: var(--a-lilac);
    --badge-color: var(--absolute-white);
  }

  &.variant-accent {
    --badge-bg: var(--a-mulberry);
    --badge-color: var(--absolute-white);
  }

  &.variant-purple-fade {
    --badge-bg: var(--a-mulberry-fade-50);
    --badge-color: var(--g-neutral-2);
  }

  &.variant-grey {
    --badge-bg: var(--g-neutral-6);
    --badge-color: var(--g-neutral-2);
  }

  &.variant-white {
    --badge-bg: var(--absolute-white);
    --badge-color: var(--absolute-black);

    #{$root}-count,
    #{$root}-label {
      box-shadow: 0 0 0 1px var(--g-neutral-6);
    }
  }

  &.variant-blue-gradient {
    --badge-color: var(--absolute-white);

    #{$root}-count,
    #{$root}-label,
    #{$root}-dot {
      background: linear-gradient(135deg, var(--a-mulberry-light), var(--a-lilac));
    }

    #{$root}-label {
      &-icon {
        background: transparent;
      }
    }
  }

  &.variant-gold-outline {
    --badge-color: var(--absolute-white);

    #{$root}-count,
    #{$root}-label {
      border: 1.5px solid transparent;
      background: linear-gradient(var(--absolute-black), var(--absolute-black)) padding-box,
                  linear-gradient(251deg, var(--s-yellow) 0%, var(--s-yellow-dark) 100%) border-box;
      font-weight: 700;
      letter-spacing: 0.01em;
    }

    #{$root}-dot {
      background: linear-gradient(251deg, var(--s-yellow) 0%, var(--s-yellow-dark) 100%);
    }
  }

  &:where(.size-md) {
    --badge-min-height: 24px;
    --badge-icon-size: 16px;
  }

  &:where(.size-sm) {
    --badge-min-height: auto;
    --badge-icon-size: 12px;
  }
}
</style>
